/**
 * UncertaintyBadge.tsx
 *
 * A badge indicator for chords with low confidence in harmonic analysis.
 * Displays a "?" badge for confidence values below 0.6 threshold.
 * Shows tooltip with confidence percentage on hover and features a pulsing
 * animation to draw attention to uncertain chord identifications.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './UncertaintyBadge.module.css';

export interface UncertaintyBadgeProps {
  /** Confidence value from 0.0 to 1.0 */
  confidence: number;
  /** Position relative to the chord */
  position: { x: number; y: number };
  /** Whether the parent element is hovered */
  isHovered?: boolean;
  /** Optional callback when badge is clicked */
  onClick?: () => void;
}

/**
 * Apple-smooth easing for UI animations
 */
const APPLE_SMOOTH = [0.4, 0.0, 0.2, 1] as const;

/**
 * Confidence threshold below which the badge is shown
 */
const CONFIDENCE_THRESHOLD = 0.6;

export const UncertaintyBadge: React.FC<UncertaintyBadgeProps> = ({
  confidence,
  position = { x: 0, y: 0 },
  isHovered = false,
  onClick,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  // Only show badge if confidence is below threshold
  if (confidence >= CONFIDENCE_THRESHOLD) {
    return null;
  }

  const confidencePercent = Math.round(confidence * 100);

  const handleMouseEnter = () => {
    setIsTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setIsTooltipVisible(false);
  };

  return (
    <motion.div
      className={styles.uncertaintyBadgeWrapper}
      style={{
        top: position.y,
        right: position.x,
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{
        scale: 1,
        opacity: isHovered ? 1 : 0.85,
      }}
      transition={{
        duration: 0.3,
        ease: APPLE_SMOOTH,
      }}
    >
      <motion.button
        className={styles.uncertaintyBadge}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        transition={{
          duration: 0.2,
          ease: APPLE_SMOOTH,
        }}
        aria-label={`Uncertain chord identification - ${confidencePercent}% confidence`}
        aria-describedby="uncertainty-tooltip"
      >
        <span className={styles.badgeText}>?</span>
      </motion.button>

      <AnimatePresence>
        {isTooltipVisible && (
          <motion.div
            id="uncertainty-tooltip"
            className={styles.tooltip}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
              duration: 0.2,
              ease: APPLE_SMOOTH,
            }}
            role="tooltip"
          >
            {confidencePercent}% confident
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

UncertaintyBadge.displayName = 'UncertaintyBadge';

export default UncertaintyBadge;
